import { useId } from 'react';

export interface SegmentOption<T extends string> {
  value: T;
  label: string;
  disabled?: boolean;
  /** Why the option is disabled (tooltip). */
  hint?: string;
}

/** A row of native radio buttons: the browser handles arrow keys and skips disabled options. */
export function SegmentedControl<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: SegmentOption<NoInfer<T>>[];
  onChange(value: NoInfer<T>): void;
}) {
  const name = useId();
  return (
    <fieldset className="min-w-0">
      <legend className="sr-only">{label}</legend>
      <div className="flex border border-line-strong">
        {options.map((o) => (
          <label key={o.value} title={o.hint} className="flex min-w-0 flex-1">
            <input
              type="radio"
              name={name}
              value={o.value}
              checked={o.value === value}
              disabled={o.disabled}
              onChange={() => onChange(o.value)}
              className="peer sr-only"
            />
            <span className="flex min-h-11 w-full cursor-pointer items-center justify-center px-2 font-display text-xs font-bold uppercase tracking-[0.12em] text-muted peer-checked:bg-accent peer-checked:text-accent-ink peer-focus-visible:ring-2 peer-focus-visible:ring-accent/60 peer-disabled:cursor-not-allowed peer-disabled:opacity-35 hover:text-text peer-checked:hover:text-accent-ink">
              {o.label}
            </span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}
